/* eslint-disable no-param-reassign */
/* eslint-disable react/no-unstable-nested-components */
/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { connect } from "react-redux";
import PropTypes from "prop-types";

// @material-ui core components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";

import { useTranslation } from "react-i18next";
import { useState } from "react";
import { Field, reduxForm } from "redux-form";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

import MDTypography from "components/MDTypography";
import { newAlertMessage } from "store/actions/globalAlertActions";

import ClientReservationEmailSection, {
  buildClientReservationExpandedStates,
  buildClientReservationStatesPayload,
} from "./emailSections/ClientReservationEmailSection";
import BookingEmailSection, {
  buildBookingExpandedStates,
  buildBookingStatesPayload,
} from "./emailSections/BookingEmailSection";
import SupportCaseEmailSection, {
  buildSupportCaseExpandedStates,
  buildSupportCaseStatesPayload,
} from "./emailSections/SupportCaseEmailSection";
import DailyUnreadChatEmailSection, {
  buildDailyUnreadChatPayload,
} from "./emailSections/DailyUnreadChatEmailSection";

const validate = () => ({});

function EmailConfig({
  handleSubmit,
  pristine,
  database,
  projectId,
  projectData,
  actNewAlertMessage,
}) {
  const { t } = useTranslation();
  const [isClientReservationExpanded, setIsClientReservationExpanded] = useState(true);
  const [expandedClientReservationStates, setExpandedClientReservationStates] = useState(
    buildClientReservationExpandedStates
  );
  const [isBookingExpanded, setIsBookingExpanded] = useState(true);
  const [expandedBookingStates, setExpandedBookingStates] = useState(buildBookingExpandedStates);
  const [isSupportCaseExpanded, setIsSupportCaseExpanded] = useState(true);
  const [expandedSupportCaseStates, setExpandedSupportCaseStates] = useState(
    buildSupportCaseExpandedStates
  );
  const [isDailyUnreadChatExpanded, setIsDailyUnreadChatExpanded] = useState(true);

  const toggleClientReservationStateExpanded = (stateKey) => {
    setExpandedClientReservationStates((prev) => ({
      ...prev,
      [stateKey]: !prev[stateKey],
    }));
  };

  const toggleBookingStateExpanded = (stateKey) => {
    setExpandedBookingStates((prev) => ({
      ...prev,
      [stateKey]: !prev[stateKey],
    }));
  };

  const toggleSupportCaseStateExpanded = (stateKey) => {
    setExpandedSupportCaseStates((prev) => ({
      ...prev,
      [stateKey]: !prev[stateKey],
    }));
  };

  const submitConfig = async (data) => {
    if (!database?.editProject || !projectId) return;

    const clientReservationData = data?.emailTemplates?.clientReservation || {};
    const clientReservationStatesPayload = buildClientReservationStatesPayload(
      clientReservationData?.states
    );

    const bookingData = data?.emailTemplates?.booking || {};
    const bookingStatesPayload = buildBookingStatesPayload(bookingData?.states);

    const supportCaseData = data?.emailTemplates?.supportCase || {};
    const supportCaseStatesPayload = buildSupportCaseStatesPayload(supportCaseData?.states);
    const dailyUnreadChatData = data?.emailTemplates?.dailyUnreadChat || {};
    const dailyUnreadChatPayload = buildDailyUnreadChatPayload(dailyUnreadChatData);

    try {
      await database.editProject({
        uid: projectId,
        emailTemplates: {
          ...(projectData?.emailTemplates || {}),
          clientReservation: {
            enabled: Boolean(clientReservationData?.enabled),
            states: clientReservationStatesPayload,
          },
          booking: {
            enabled: Boolean(bookingData?.enabled),
            states: bookingStatesPayload,
          },
          supportCase: {
            enabled: Boolean(supportCaseData?.enabled),
            states: supportCaseStatesPayload,
          },
          dailyUnreadChat: dailyUnreadChatPayload,
        },
      });

      actNewAlertMessage({
        type: "success",
        title: t("emailConfig"),
        content: `${t("configurationSent")}`,
      });
    } catch (error) {
      actNewAlertMessage({
        type: "error",
        title: t("emailConfig"),
        content: `${t("Save")} ${t("failed")}`,
      });
    }
  };

  return (
    <form onSubmit={handleSubmit(submitConfig)}>
      <Card id="email-config" sx={{ overflow: "visible" }}>
        <MDBox p={3}>
          <MDTypography variant="h4">{t("emailConfig").toUpperCase()}</MDTypography>
        </MDBox>
        <MDBox pb={3} px={3}>
          <Grid container spacing={3}>
            <Field
              name="emailTemplates.clientReservation.enabled"
              component={({ input: templateInput }) => (
                <ClientReservationEmailSection
                  expandedStates={expandedClientReservationStates}
                  isTemplateExpanded={isClientReservationExpanded}
                  onToggleStateExpanded={toggleClientReservationStateExpanded}
                  onToggleTemplateExpanded={() => setIsClientReservationExpanded((prev) => !prev)}
                  t={t}
                  templateInput={templateInput}
                />
              )}
            />

            <Field
              name="emailTemplates.booking.enabled"
              component={({ input: templateInput }) => (
                <BookingEmailSection
                  expandedStates={expandedBookingStates}
                  isTemplateExpanded={isBookingExpanded}
                  onToggleStateExpanded={toggleBookingStateExpanded}
                  onToggleTemplateExpanded={() => setIsBookingExpanded((prev) => !prev)}
                  t={t}
                  templateInput={templateInput}
                />
              )}
            />

            <Field
              name="emailTemplates.supportCase.enabled"
              component={({ input: templateInput }) => (
                <SupportCaseEmailSection
                  expandedStates={expandedSupportCaseStates}
                  isTemplateExpanded={isSupportCaseExpanded}
                  onToggleStateExpanded={toggleSupportCaseStateExpanded}
                  onToggleTemplateExpanded={() => setIsSupportCaseExpanded((prev) => !prev)}
                  t={t}
                  templateInput={templateInput}
                />
              )}
            />

            <Field
              name="emailTemplates.dailyUnreadChat.enabled"
              component={({ input: templateInput }) => (
                <DailyUnreadChatEmailSection
                  isTemplateExpanded={isDailyUnreadChatExpanded}
                  onToggleTemplateExpanded={() => setIsDailyUnreadChatExpanded((prev) => !prev)}
                  t={t}
                  templateInput={templateInput}
                />
              )}
            />

            <Grid size={{ xs: 12 }}>
              <MDBox display="flex" justifyContent="center" mt={2} xs={12}>
                <MDButton
                  type="submit"
                  variant="gradient"
                  color="dark"
                  size="small"
                  disabled={pristine}
                >
                  {t("Save")}
                </MDButton>
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>
      </Card>
    </form>
  );
}

EmailConfig.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  pristine: PropTypes.bool.isRequired,
  actNewAlertMessage: PropTypes.func.isRequired,
  database: PropTypes.object,
  projectId: PropTypes.string,
  projectData: PropTypes.object,
};

EmailConfig.defaultProps = {
  database: null,
  projectId: "",
  projectData: {},
};

const mapStateToProps = (state, props) => {
  const { projectsReducer = {} } = state;
  const projectData = projectsReducer[props?.projectId] || {};
  const clientReservationTemplate = projectData?.emailTemplates?.clientReservation || {};
  const bookingTemplate = projectData?.emailTemplates?.booking || {};
  const supportCaseTemplate = projectData?.emailTemplates?.supportCase || {};
  const dailyUnreadChatTemplate = projectData?.emailTemplates?.dailyUnreadChat || {};

  const clientReservationStatesInitialValues = buildClientReservationStatesPayload(
    clientReservationTemplate?.states
  );
  const bookingStatesInitialValues = buildBookingStatesPayload(bookingTemplate?.states);
  const supportCaseStatesInitialValues = buildSupportCaseStatesPayload(supportCaseTemplate?.states);
  const dailyUnreadChatInitialValues = buildDailyUnreadChatPayload(dailyUnreadChatTemplate);

  return {
    projectData,
    initialValues: {
      emailTemplates: {
        clientReservation: {
          enabled: Boolean(clientReservationTemplate?.enabled),
          states: clientReservationStatesInitialValues,
        },
        booking: {
          enabled: Boolean(bookingTemplate?.enabled),
          states: bookingStatesInitialValues,
        },
        supportCase: {
          enabled: Boolean(supportCaseTemplate?.enabled),
          states: supportCaseStatesInitialValues,
        },
        dailyUnreadChat: dailyUnreadChatInitialValues,
      },
    },
  };
};

const mapDispatchToProps = (dispatch) => ({
  actNewAlertMessage: (message) => dispatch(newAlertMessage(message)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(
  reduxForm({
    form: "EmailConfig", // a unique identifier for this form
    enableReinitialize: true,
    validate,
  })(EmailConfig)
);
